import React, { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Menu as MuiMenu, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { GroupFragment, ReportNames, ReportingFilter, useUserOverviewStatsQuery } from 'api';
import { useProtectedClient, useDateRange, useNotify, useReportingFilter } from 'hooks';
import { CustomSVG, DatePickers, PaddedPaper } from 'components';

import {
  ActiveUserStatsChart,
  DailyUserStatsChart,
  Filters,
  FilterChips,
  UserDashboardStatTable,
} from '../components/Analytics';
import { ClearFiltersButton, RangePickerWrapper } from '../components/shared';

const useStyles = makeStyles(theme => ({
  button: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[700],
    },
  },
}));

const AnalyticsUsers: FC = () => {
  const classes = useStyles();
  const client = useProtectedClient();
  const notify = useNotify();
  const { t } = useTranslation();
  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } = useDateRange();
  const { filter, flattenedFilter, clearFilter, setFilter, removeFilterItem } =
    useReportingFilter();
  const [anchorEl, setAnchorEl] = useState(null);
  const [requestReport, setRequestReport] = useState(false);
  const [report, setReport] = useState(null);
  const [cachedGroups, setCachedGroups] = useState([]);

  useUserOverviewStatsQuery(
    {
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      report,
      filter,
    },
    {
      enabled: requestReport,
      onSuccess: () => notify.info({ message: t('analytics:reportRequested') }),
      onError: () => notify.error({ message: t('analytics:reportRequestedError') }),
      onSettled: () => setRequestReport(false),
    }
  );

  function handleOpen(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleApplyFilter(nextFilter: ReportingFilter, selectedGroups: GroupFragment[]) {
    setCachedGroups(selectedGroups);
    setFilter(nextFilter);
  }

  function handleRemoveFilterItem(id: string) {
    const removedGroupArray = cachedGroups.filter(cachedGroup => cachedGroup.id !== id);

    setCachedGroups(removedGroupArray);
    removeFilterItem(id);
  }

  function handleReportRequest(nextReport: ReportNames) {
    setReport(nextReport);
    setRequestReport(true);
    handleClose();
  }

  function renderDownloadMenu() {
    return (
      <Fragment>
        <MuiMenu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
          <MenuItem onClick={() => handleReportRequest(ReportNames.UserInternal)}>
            {t('analytics:userInternal')}
          </MenuItem>
          <MenuItem onClick={() => handleReportRequest(ReportNames.UserExternal)}>
            {t('analytics:userExternal')}
          </MenuItem>
        </MuiMenu>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Box mb={2}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Typography variant="h5" color="textSecondary">
              {t('analytics:active_users')}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              size="large"
              className={classes.button}
              variant="contained"
              startIcon={<CustomSVG name="download" />}
              onClick={handleOpen}
            >
              {t('analytics:requestReport')}
            </Button>
            {renderDownloadMenu()}
          </Grid>
        </Grid>
      </Box>
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item>
          <PaddedPaper>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item>
                    <RangePickerWrapper>
                      <DatePickers.Range
                        dateRange={dateRange}
                        startDate={startDate}
                        endDate={endDate}
                        onSetStart={setStartDate}
                        onSetEnd={setEndDate}
                        onSetRange={setDateRange}
                      />
                    </RangePickerWrapper>
                  </Grid>
                  <Grid item>
                    <Box display="flex" height="100%">
                      <Filters
                        filter={filter}
                        onSubmit={handleApplyFilter}
                        selectedGroups={cachedGroups}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {!!flattenedFilter.length && (
                <Grid item>
                  <ClearFiltersButton onClick={clearFilter} />
                </Grid>
              )}
            </Grid>
            {!!flattenedFilter.length && (
              <Grid container>
                <Grid item>
                  <FilterChips
                    flattenedFilter={flattenedFilter}
                    onRemove={handleRemoveFilterItem}
                    selectedGroups={cachedGroups}
                  />
                </Grid>
              </Grid>
            )}
          </PaddedPaper>
        </Grid>
        <Grid item>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <ActiveUserStatsChart startDate={startDate} endDate={endDate} filter={filter} />
            </Grid>
            <Grid item xs={8}>
              <DailyUserStatsChart startDate={startDate} endDate={endDate} filter={filter} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <UserDashboardStatTable startDate={startDate} endDate={endDate} filter={filter} />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default AnalyticsUsers;
